import {
  scheduleTimeToMinutes,
  validateWeeklySchedule,
} from '../../../common/addressSchedule/scheduleUtils.ts';

type FreightRow = Record<string, unknown>;

const trimText = (value: unknown): string => String(value ?? '').trim();

const readFreteArea = (row: FreightRow): string =>
  String(row.area ?? row.bairro_ou_cep ?? row.bairro ?? row.cep ?? '');

const readFreteKm = (row: FreightRow): string =>
  String(row.km ?? row.raio ?? row.distancia ?? '');

const readFreteTempo = (row: FreightRow): string =>
  String(row.tempo ?? row.tempo_estimado ?? '');

const readFretePreco = (row: FreightRow): string =>
  String(row.preco ?? row.valor ?? row.taxa ?? '');

const normalizeFreteValue = (value: string): string => trimText(value);

const parseFreteNumber = (value: string): number | null => {
  const normalized = normalizeFreteValue(value).replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

export type FormPromptValidationInput = {
  form: any;
  hasType: (key: string) => boolean;
  pedidosFeatureOn: boolean;
  modoPedidos: 'whatsapp' | 'link' | null;
  reservasFeatureOn: boolean;
  modoReservas: 'whatsapp' | 'link' | null;
  showHorarios: boolean;
  showEndereco: boolean;
  showCidadeEstado: boolean;
  reservaOrigemEfetiva: 'reservo' | 'externo';
  usaCvVagas: boolean;
  cvPublicLink: string;
  isAtendabotBrand: boolean;
  hasFeedbackHeroProduct: boolean;
};

export type FormPromptValidationResult = {
  errors: Record<string, string>;
  warnings: Record<string, string>;
};

export function validateFormPrompt({
  form,
  hasType,
  pedidosFeatureOn,
  modoPedidos,
  reservasFeatureOn,
  modoReservas,
  showHorarios,
  showEndereco,
  showCidadeEstado,
  reservaOrigemEfetiva,
  usaCvVagas,
  cvPublicLink,
  isAtendabotBrand,
  hasFeedbackHeroProduct,
}: FormPromptValidationInput): FormPromptValidationResult {
    const nextErrors: Record<string, string> = {};
    const nextWarnings: Record<string, string> = {};
    const requiredText = (value: string, key: string, message = 'Informe este campo.') => {
      if (!value || !value.trim()) nextErrors[key] = message;
    };
    const setError = (key: string, ruleId: string, message: string) => {
      if (!nextErrors[key]) nextErrors[key] = message;
    };
    const setWarning = (key: string, ruleId: string, message: string) => {
      if (!nextWarnings[key]) nextWarnings[key] = message;
    };

    requiredText(form.nome_empresa, 'nome_empresa');

    if (pedidosFeatureOn && modoPedidos === null) {
      setError(
        'modo_pedidos',
        'modo_pedidos',
        'Escolha como a IA deve tratar os pedidos: registrar na conversa ou apenas enviar o link do cardápio.',
      );
    }

    if (reservasFeatureOn && modoReservas === null) {
      setError(
        'modo_reservas',
        'modo_reservas',
        'Escolha como a IA deve tratar as reservas: registrar na conversa ou apenas enviar o link.',
      );
    }

    if (showHorarios) {
      const allClosed =
        form.horarios_semana.length > 0 && form.horarios_semana.every((day) => day.closed);
      if (allClosed) {
        setError(
          'horarios_semana',
          'horarios_semana',
          'Defina pelo menos 1 dia/horário aberto.',
        );
      }

      form.horarios_semana.forEach((day) => {
        if (day.closed) return;

        if (!Array.isArray(day.periods) || day.periods.length === 0) {
          setError(
            'horarios_semana',
            `horarios_semana_${day.day}`,
            `Informe ao menos um período válido para ${day.label}.`,
          );
          return;
        }

        const intervals = day.periods.map((period, index) => {
          const startMinutes = scheduleTimeToMinutes(period.start);
          const endMinutes = scheduleTimeToMinutes(period.end);

          if (startMinutes === null || endMinutes === null || startMinutes === endMinutes) {
            setError(
              'horarios_semana',
              `horarios_semana_${day.day}_${index}`,
              `Período inválido em ${day.label}. O início deve ser menor que o fim.`,
            );
          }

          return { startMinutes, endMinutes };
        });

        intervals
          .filter((item) => item.startMinutes !== null && item.endMinutes !== null)
          .sort((a, b) => (a.startMinutes as number) - (b.startMinutes as number))
          .forEach((interval, index, sorted) => {
            if (index === 0) return;
            const previous = sorted[index - 1];
            if ((interval.startMinutes as number) < (previous.endMinutes as number)) {
              setError(
                'horarios_semana',
                `horarios_semana_overlap_${day.day}`,
                `Há períodos sobrepostos em ${day.label}.`,
              );
            }
          });
      });

      validateWeeklySchedule(form.horarios_semana).issues.forEach((issue) => {
        setError('horarios_semana', issue.key, issue.message);
      });
    }

    const enderecoObrigatorio =
      showEndereco &&
      (!hasType('encomendas') ||
        ['pedidos_delivery', 'pedidos_retirada', 'consumo_no_local', 'reservas', 'duvidas_gerais'].some(hasType) ||
        form.entrega_ativa === true ||
        form.retirada_ativa === true);

    if (enderecoObrigatorio) {
      if (!form.rua?.trim()) {
        setError('rua', 'endereco_retirada', 'Informe o endereço do negócio.');
      }
      if (!form.bairro?.trim()) {
        setError('bairro', 'endereco_retirada', 'Informe o endereço do negócio.');
      }
      if (!form.cep?.trim()) {
        setError('cep', 'endereco_retirada', 'Informe o endereço do negócio.');
      }
    }

    if (showEndereco) {
      const lat = form.endereco_lat ? Number(form.endereco_lat) : null;
      const long = form.endereco_long ? Number(form.endereco_long) : null;
      const hasLat = lat !== null;
      const hasLong = long !== null;

      if (hasLat !== hasLong) {
        setError('endereco_lat', 'geo_pair', 'Latitude e longitude devem ser informadas juntas.');
        setError('endereco_long', 'geo_pair', 'Latitude e longitude devem ser informadas juntas.');
      } else if (hasLat && hasLong) {
        if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
          setError('endereco_lat', 'geo_lat_range', 'Latitude inválida. Use um valor entre -90 e 90.');
        }
        if (!Number.isFinite(long) || long < -180 || long > 180) {
          setError('endereco_long', 'geo_long_range', 'Longitude inválida. Use um valor entre -180 e 180.');
        }
      }

      const cepDigits = String(form.cep || '').replace(/\D/g, '');
      if (form.cep?.trim() && cepDigits.length !== 8) {
        setError('cep', 'cep_format', 'CEP inválido. Use 8 dígitos.');
      }

      const uf = String(form.estado || '').trim().toUpperCase();
      if (uf && !/^[A-Z]{2}$/.test(uf)) {
        setError('estado', 'uf_format', 'UF inválida. Use 2 letras.');
      }
    }

    if (showCidadeEstado) {
      if (!form.cidade?.trim() || !form.estado?.trim()) {
        setError('cidade', 'cidade_estado', 'Informe a cidade/UF para eventos.');
        setError('estado', 'cidade_estado', 'Informe a cidade/UF para eventos.');
      }
    }

    if (modoPedidos === 'whatsapp' && form.metodos_pagamento.length === 0) {
      setError(
        'metodos_pagamento',
        'metodos_pagamento',
        'Para registrar pedidos pelo WhatsApp, selecione pelo menos 1 forma de pagamento.',
      );
    }

    if (form.metodos_pagamento.includes('pix') && !form.pix_chave?.trim()) {
      setError('pix_chave', 'pix_chave', 'Você marcou Pix. Informe a chave Pix.');
    }

    if (modoPedidos !== null) {
      const hasCardapio =
        form.usar_cardapio_onemenu === true ||
        form.cardapio_cadastrado === true ||
        Boolean(form.link_cardapio?.trim());
      if (!hasCardapio) {
        setError(
          'cardapio_cadastrado',
          'cardapio',
          modoPedidos === 'whatsapp'
            ? 'Para registrar pedidos, conecte o OneMenu ou informe o link do cardápio.'
            : 'Para enviar o cardápio ao cliente, conecte o OneMenu ou informe o link.',
        );
        if (!form.usar_cardapio_onemenu) {
          setError('link_cardapio', 'cardapio', 'Informe o link do cardápio ou conecte o OneMenu.');
        }
      }
    }

    if (modoPedidos === 'whatsapp' && form.entrega_ativa !== true && form.retirada_ativa !== true) {
      setError(
        'entrega_ativa',
        'pedidos_fulfillment',
        'Para registrar pedidos, ative entrega, retirada ou ambos em “Entrega e retirada”.',
      );
    }

    if (hasType('pedidos_delivery')) {
      if (form.entrega_ativa !== true) {
        setError('entrega_ativa', 'entrega_ativa', "Você selecionou Delivery. Ative 'Entrega'.");
      }
      const entregaMin = Number(form.tempo_estimado_entrega_min || 0);
      if (form.tempo_estimado_entrega_min && entregaMin <= 5) {
        setWarning('tempo_estimado_entrega_min', 'tempo_baixo', 'Tempo de entrega muito baixo.');
      }
      if (form.modelo_frete && !form.regra_fora_da_area?.trim()) {
        setWarning('regra_fora_da_area', 'frete_area', 'Modelo de frete sem regra fora da área.');
      }
    }

    if (form.entrega_ativa === true && form.modelo_frete === 'bairros_cep') {
      if (!Array.isArray(form.tabela_frete) || form.tabela_frete.length === 0) {
        setError('tabela_frete', 'frete_bairro_rows', 'Adicione ao menos 1 linha com bairro, tempo e valor.');
      } else {
        const hasInvalidRow = form.tabela_frete.some((row) => (
          !normalizeFreteValue(readFreteArea(row || {})) ||
          !normalizeFreteValue(readFreteTempo(row || {})) ||
          !normalizeFreteValue(readFretePreco(row || {}))
        ));
        if (hasInvalidRow) {
          setError('tabela_frete', 'frete_bairro_fields', 'Cada linha precisa de bairro, tempo e valor.');
        }
      }
    }

    if (form.entrega_ativa === true && form.modelo_frete === 'raio') {
      if (!Array.isArray(form.tabela_frete) || form.tabela_frete.length === 0) {
        setError('tabela_frete', 'frete_raio_rows', 'Informe pelo menos 1 faixa com km e valor.');
      } else {
        const hasInvalidRow = form.tabela_frete.some((row) => {
          const km = parseFreteNumber(readFreteKm(row || {}));
          const preco = parseFreteNumber(readFretePreco(row || {}));
          return km === null || km <= 0 || preco === null || preco < 0;
        });
        if (hasInvalidRow) {
          setError('tabela_frete', 'frete_raio_fields', 'Cada faixa por raio precisa de km válido (> 0) e valor de frete.');
        }
        const radiusValues = form.tabela_frete
          .map((row) => parseFreteNumber(readFreteKm(row || {})))
          .filter((value): value is number => value !== null);
        if (new Set(radiusValues).size !== radiusValues.length) {
          setError('tabela_frete', 'frete_raio_duplicate', 'Não repita a mesma distância em mais de uma faixa.');
        }
      }
    }

    if (form.entrega_ativa === true && form.modelo_frete === 'fixa') {
      if (!Array.isArray(form.tabela_frete) || form.tabela_frete.length === 0) {
        setError('tabela_frete', 'frete_fixa_rows', 'Informe pelo menos 1 linha com tempo e valor da taxa fixa.');
      } else {
        const hasInvalidRow = form.tabela_frete.some((row) => (
          !normalizeFreteValue(readFreteTempo(row || {})) ||
          !normalizeFreteValue(readFretePreco(row || {}))
        ));
        if (hasInvalidRow) {
          setError('tabela_frete', 'frete_fixa_fields', 'A taxa fixa precisa de tempo e valor em cada linha.');
        }
      }
    }

    if (hasType('pedidos_retirada')) {
      if (form.retirada_ativa !== true) {
        setError('retirada_ativa', 'retirada_ativa', "Você selecionou Retirada. Ative 'Retirada'.");
      }
    }

    if (hasType('consumo_no_local') || hasType('reservas')) {
      if (form.possui_atendimento_presencial !== true) {
        setError(
          'possui_atendimento_presencial',
          'presencial',
          'Ative atendimento presencial.',
        );
      }
    }

    if (hasType('encomendas')) {
      if (!(form.entrega_ativa === true || form.retirada_ativa === true)) {
        setError('entrega_ativa', 'encomendas_entrega', 'Ative entrega ou retirada.');
        setError('retirada_ativa', 'encomendas_retirada', 'Ative entrega ou retirada.');
      }
      if (!form.frases_ou_regras_especiais?.trim()) {
        setError(
          'frases_ou_regras_especiais',
          'frases_ou_regras_especiais',
          'Informe as regras/observações para este tipo de atendimento.',
        );
      }
    }

    if (hasType('eventos')) {
      if (!form.frases_ou_regras_especiais?.trim()) {
        setError(
          'frases_ou_regras_especiais',
          'frases_ou_regras_especiais',
          'Informe as regras/observações para este tipo de atendimento.',
        );
      }
    }

    if (hasType('vagas_fornecedores_feedback')) {
      const hasContact =
        (usaCvVagas && Boolean(cvPublicLink)) ||
        Boolean(form.email_atendimento?.trim()) ||
        Boolean(form.site?.trim());
      if (!hasContact) {
        setError(
          'link_vagas',
          'links_canais',
          'Informe pelo menos 1 link ou canal (Currículo Certo, site ou e-mail).',
        );
        setError(
          'email_atendimento',
          'links_canais',
          'Informe pelo menos 1 link ou canal (Currículo Certo, site ou e-mail).',
        );
        setError(
          'site',
          'links_canais',
          'Informe pelo menos 1 link ou canal (Currículo Certo, site ou e-mail).',
        );
      }
    }

    if (modoReservas === 'link' && reservaOrigemEfetiva === 'externo' && !trimText(form.link_reserva_externo)) {
      setError(
        'link_reserva_externo',
        'reservas_link',
        'Informe o link de reservas que a IA deve enviar aos clientes.',
      );
    }

    if (hasType('reservas')) {
      const hasContato =
        Boolean(form.telefone_ligacoes?.trim()) ||
        Boolean(form.email_atendimento?.trim()) ||
        Boolean(form.site?.trim());
      if (!hasContato) {
        setWarning('contato_reservas', 'reservas_contato', 'Reservas sem telefone, site ou e-mail.');
      }
    }

    if (!isAtendabotBrand && form.usar_feedbackhero_avaliacao_pedidos === true && !hasFeedbackHeroProduct) {
      setError(
        'usar_feedbackhero_avaliacao_pedidos',
        'feedbackhero_access',
        'Conecte o FeedbackHero para solicitar avaliações depois dos pedidos.',
      );
    }

    return { errors: nextErrors, warnings: nextWarnings };
}
